import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import AddVideoModal from './components/AddVideoModal';
import {
  createVideo, getVideosByManager, getVideoProgress, deleteVideo
} from '../../api_service';

const VideoManagement = () => {
  const navigate = useNavigate();
  const managerId = sessionStorage.getItem('userId');

  const [videos, setVideos] = useState([]);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [progress, setProgress] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadVideos(); }, []);

  const loadVideos = async () => {
    setLoading(true);
    try {
      const res = await getVideosByManager(managerId);
      setVideos(Array.isArray(res?.data) ? res.data : []);
    } catch {
      setVideos([]);
    } finally {
      setLoading(false);
    }
  };

  const loadProgress = async (videoId) => {
    try {
      const res = await getVideoProgress(videoId);
      setProgress(Array.isArray(res?.data) ? res.data : []);
    } catch {
      setProgress([]);
    }
  };

  const handleVideoClick = async (item) => {
    setSelectedVideo(item);
    await loadProgress(item.video.videoId);
  };

  const handleCreateVideo = async (payload) => {
    await createVideo(managerId, payload);
    await loadVideos();
  };

  const handleDelete = async (videoId, e) => {
    e.stopPropagation();
    if (!window.confirm('Delete this video lesson?')) return;
    await deleteVideo(videoId);
    if (selectedVideo?.video?.videoId === videoId) {
      setSelectedVideo(null);
      setProgress([]);
    }
    await loadVideos();
  };

  const deadlineLabel = (dateStr) => {
    if (!dateStr) return '';
    const diff = Math.ceil((new Date(dateStr) - new Date()) / 86400000);
    if (diff < 0) return { text: `${Math.abs(diff)}d overdue`, cls: 'text-error' };
    if (diff === 0) return { text: 'Due today', cls: 'text-warning' };
    if (diff <= 3) return { text: `${diff}d left`, cls: 'text-warning' };
    return { text: `${diff}d left`, cls: 'text-muted-foreground' };
  };

  const totalAssigned = videos.reduce((s, v) => s + (v.totalAssigned || 0), 0);
  const totalCompleted = videos.reduce((s, v) => s + (v.completed || 0), 0);
  const completionRate = totalAssigned > 0 ? Math.round((totalCompleted / totalAssigned) * 100) : 0;

  const watchedPct = (p) => {
    if (p?.completed) return 100;
    const dur = selectedVideo?.video?.durationSeconds;
    if (!dur || dur <= 0) return p?.watchedSeconds > 0 ? 0 : 0;
    return Math.min(100, Math.round(((p?.watchedSeconds || 0) / dur) * 100));
  };

  return (
    <div className="min-h-screen bg-background">
      <Header
        userName={sessionStorage.getItem('userName') || 'User'}
        userRole="manager"
        onLogout={() => navigate('/')}
      />
      <main className="pt-16 max-w-7xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-foreground">Video Lessons</h1>
            <p className="text-sm text-muted-foreground mt-1">Assign training videos and track watch progress</p>
          </div>
          <Button onClick={() => setShowAdd(true)} iconName="Plus" iconSize={16}>
            Add Video
          </Button>
        </div>

        {/* Summary cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          {[
            { label: 'Total Videos', value: videos.length, icon: 'Video', color: 'text-primary' },
            { label: 'Total Assigned', value: totalAssigned, icon: 'Users', color: 'text-accent' },
            { label: 'Completion Rate', value: `${completionRate}%`, icon: 'CheckCircle', color: 'text-success' },
          ].map(card => (
            <div key={card.label} className="bg-card border border-border rounded-xl p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center">
                  <Icon name={card.icon} size={18} className={card.color} />
                </div>
                <div>
                  <p className="text-2xl font-bold text-foreground">{card.value}</p>
                  <p className="text-xs text-muted-foreground">{card.label}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          {/* Video list */}
          <div className="lg:col-span-2 bg-card border border-border rounded-xl overflow-hidden">
            <div className="p-4 border-b border-border">
              <h2 className="font-semibold text-foreground">Videos ({videos.length})</h2>
            </div>
            {loading ? (
              <div className="p-8 text-center text-muted-foreground text-sm">Loading...</div>
            ) : videos.length === 0 ? (
              <div className="p-8 text-center">
                <Icon name="Video" size={40} className="text-muted-foreground mx-auto mb-3" />
                <p className="text-sm text-muted-foreground">No videos yet. Add your first video.</p>
              </div>
            ) : (
              <div className="divide-y divide-border overflow-y-auto max-h-[500px]">
                {videos.map(item => {
                  const dl = deadlineLabel(item.video?.deadline);
                  const isSelected = selectedVideo?.video?.videoId === item.video?.videoId;
                  return (
                    <div
                      key={item.video?.videoId}
                      onClick={() => handleVideoClick(item)}
                      className={`p-4 cursor-pointer hover:bg-muted/50 transition-colors ${isSelected ? 'bg-primary/5 border-l-2 border-primary' : ''}`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <p className="text-sm font-medium text-foreground line-clamp-2">{item.video?.title}</p>
                        <button
                          onClick={(e) => handleDelete(item.video?.videoId, e)}
                          className="text-muted-foreground hover:text-error shrink-0"
                          title="Delete"
                        >
                          <Icon name="Trash2" size={15} />
                        </button>
                      </div>
                      <div className="flex items-center gap-3 mt-2">
                        <span className="text-xs text-muted-foreground">{item.totalAssigned} assigned</span>
                        {item.completed > 0 && (
                          <span className="text-xs bg-success/10 text-success px-2 py-0.5 rounded-full">{item.completed} completed</span>
                        )}
                        {dl && <span className={`text-xs whitespace-nowrap ml-auto ${dl.cls}`}>{dl.text}</span>}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Progress panel */}
          <div className="lg:col-span-3 bg-card border border-border rounded-xl overflow-hidden">
            {!selectedVideo ? (
              <div className="h-full flex flex-col items-center justify-center p-12 text-center">
                <Icon name="MousePointerClick" size={40} className="text-muted-foreground mb-3" />
                <p className="text-sm text-muted-foreground">Select a video to view trainee progress</p>
              </div>
            ) : (
              <>
                <div className="p-4 border-b border-border">
                  <h2 className="font-semibold text-foreground">{selectedVideo.video?.title}</h2>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {selectedVideo.video?.description || 'No description'}
                  </p>
                  <div className="flex gap-4 mt-2 text-xs text-muted-foreground">
                    <span>Deadline: {selectedVideo.video?.deadline || 'None'}</span>
                    {selectedVideo.video?.durationSeconds != null && (
                      <span>Length: {selectedVideo.video.durationSeconds}s</span>
                    )}
                  </div>
                </div>
                <div className="divide-y divide-border overflow-y-auto max-h-[460px]">
                  {progress.length === 0 ? (
                    <div className="p-8 text-center text-sm text-muted-foreground">No trainees assigned</div>
                  ) : (
                    progress.map(p => {
                      const pct = watchedPct(p);
                      return (
                        <div key={p.progressId} className="p-4">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="text-sm font-medium text-foreground">
                                {p.trainee?.firstname} {p.trainee?.lastname}
                              </p>
                              <p className="text-xs text-muted-foreground">{p.trainee?.emailid}</p>
                            </div>
                            <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${p.completed ? 'bg-success/10 text-success' : 'bg-warning/10 text-warning'}`}>
                              {p.completed ? 'Completed' : 'In Progress'}
                            </span>
                          </div>
                          <div className="flex items-center gap-3 mt-2">
                            <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
                              <div
                                className={`h-full ${p.completed ? 'bg-success' : 'bg-primary'}`}
                                style={{ width: `${pct}%` }}
                              />
                            </div>
                            <span className="text-xs text-muted-foreground w-10 text-right">{pct}%</span>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </main>

      {showAdd && (
        <AddVideoModal
          managerId={managerId}
          onClose={() => setShowAdd(false)}
          onCreated={handleCreateVideo}
        />
      )}
    </div>
  );
};

export default VideoManagement;
